import React, {useEffect, useState} from "react";
import {useTranslation} from 'react-i18next';
import {useToast} from '../../modules/layout/components/ToastContext';
import {deleteById, getAll} from "../_request";
import {IFilterTemplateClaimModel, ITemplateClaimDto} from "../../interfaces/IUtilities";
import {Link, useNavigate} from "react-router-dom";
import Loader from "../../modules/layout/components/Loader";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import {AxiosResponse} from "axios";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import {Pencil, Trash3} from 'react-bootstrap-icons';
import {Field, Form, Formik} from "formik";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import SortDirection from "../../modules/layout/components/SortDirection";
import Pagination from "../../modules/layout/components/Pagination";


const TemplateClaimList = () => {
    const {t} = useTranslation();
    const {showToast} = useToast();
    const [templateClaims, setTemplateClaims] = useState([] as ITemplateClaimDto[]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("name");
    const [asc, setAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedName, setSelectedName] = useState<string | null>(null);

    const initialFormState = {
        id: '',
        name: '',
        code: '',
    }
    let order = asc ? "asc" : "desc";
    const [activeFilters, setActiveFilters] = useState<IFilterTemplateClaimModel>(initialFormState);

    const fetchTemplateClaims = async (filters: IFilterTemplateClaimModel = activeFilters) => {
        try {

            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];
            //if (filters.id) params.push({ key: 'id', value: filters.id });
            if (filters.id) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.id))) {
                    params.push({ key: 'id', value: filters.id });
                } else {
                    showToast(t("IdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            };
            if (filters.name) params.push({ key: 'name', value: filters.name });

            let result = await getAll('/api/templateclaims/getall/',
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data as any;
            dataResults = dataResults["items"] as ITemplateClaimDto[];
            setTotal(result?.data.totalCount);
            setTotalPages(Math.ceil(result?.data.totalCount ?? 0 / result?.data.pageSize));

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTemplateClaims(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', { error: String(error) }));
            showToast(t('ConnectionError', { error: String(error) }), 'error');
            navigate('/');
        }
    }

    useEffect(() => {
        fetchTemplateClaims();
    }, [navigate, showToast, page, sortField, asc, pageSize]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    const handleDeleteClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        if (selectedId) {
            try {
                let result = await deleteById("/api/templateclaims", selectedId) as AxiosResponse;
                let respStatus = result.status;

                if (respStatus === 200) {
                    showToast('Predefined template claim with logic deleted successfully!', 'success');
                    setLoading(false);
                    await fetchTemplateClaims();
                } else if (respStatus === 403) {
                    navigate("/error/403");
                } else if (respStatus === 401) {
                    navigate("/auth/login");
                } else if (respStatus === 500) {
                    navigate("/error/500");
                } else {
                    showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                    throw new Error(t('StatusCode', { code: String(respStatus) }));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', { error: String(error) }), 'error');
            }

        }
        setShowModal(false);
        setSelectedId(null);
    };

    const handleCancelDelete = () => {
        setShowModal(false);
        setSelectedId(null);
    };

    const handleFilterSearch = async (filters: IFilterTemplateClaimModel) => {
        setActiveFilters(filters);
        await fetchTemplateClaims(filters);
    };

    return (
        <div className="gg-templateClaimList container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t('Home'), href: "/" },
                    { title: t('PredefinedTemplateClaimList') } // Active page (no href)
                ]}/>

            <h3 className="mb-3">{t('PredefinedTemplateClaimList')}</h3>

            {loading && (<Loader />)}

            <button className="btn btn-success gg-btn-submit mb-3" onClick={() => navigate("/templateclaims/create")}>
                {t('Create')}
            </button>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm }) => (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Id')}
                                        <Field className="form-control" name="id"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Name')}
                                        <Field className="form-control" name="name"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3 align-self-end">
                                    <button type="submit" className="btn gg-btn-submit mb-2 me-2">
                                        {t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState);
                                            }}>
                                        {t('Reset')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                    <tr>
                        <th style={{width: "75%"}} onClick={() => handleSort("name")}>{t('Name')}
                            <SortDirection isActive={sortField === 'name'} order={order}/>
                        </th>
                        <th className="text-wrap" style={{width: "25%"}}>{t('Actions')}</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {templateClaims.map(s => (
                        <tr key={s.id}>
                            <td>{s.name}</td>
                            <td className="text-wrap">
                                <Link title={t('Edit')} to={`/templateclaims/edit/${s.id}`} className="btn gg-btn btn-sm me-2">
                                    <Pencil size={16} className="text-danger" />
                                </Link>
                                <button title={t('Delete')} className="btn btn-danger btn-sm me-2"
                                        onClick={() => handleDeleteClick(s.id, s.name)}>
                                    <Trash3 size={16}/>
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                show={showModal}
                title={t('Delete')}
                message={t('DeleteItemWithName', { name: String(selectedName) })}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                loading={loading}
            />

            <Pagination currentPage={page}
                onPageChange={(page) => { setPage(page) }}
                totalItems={total}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size) }} />
        </div>
    );
};

export default TemplateClaimList;
